import {
  CallRecordingOverrideSetting,
  CallRecordingSetting,
  OnePartyConsentRecording,
  TwoPartyConsentRecording,
} from '../api/graphql/types';

export interface CallRecordingToggleSettings {
  initialCallRecordingToggleValue: boolean;
  callRecordingToggleVisible: boolean;
  callRecordingToggleDisabled: boolean;
}

const CALL_RECORDED_STORAGE_KEY = 'userCallRecordingPreference';

export const callRecordingToggleSettings = (
  oneSidedCallRecordingFeatureFlag: boolean,
  callRecordingSetting: CallRecordingSetting | null | undefined,
  onePartyConsentRecordingSetting: OnePartyConsentRecording | null | undefined,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
  numberRequiresConsent: boolean,
): CallRecordingToggleSettings => {
  // Default toggle settings
  const defaultToggleSettings: CallRecordingToggleSettings = {
    initialCallRecordingToggleValue: false,
    callRecordingToggleVisible: true,
    callRecordingToggleDisabled: true,
  };

  // Early return to handle case when FF is off
  if (!oneSidedCallRecordingFeatureFlag) {
    if (!callRecordingSetting || callRecordingSetting === 'DISABLED') {
      return defaultToggleSettings;
    }
    if (callRecordingSetting === 'OPTIONAL') {
      return {
        initialCallRecordingToggleValue: false,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: false,
      };
    }
    if (callRecordingSetting === 'REQUIRED') {
      return {
        initialCallRecordingToggleValue: true,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: true,
      };
    }
  }

  // numberRequiresConsent = two party consent region
  if (numberRequiresConsent) {
    if (twoPartyConsentRecordingSetting === 'DISABLED') {
      if (onePartyConsentRecordingSetting !== 'DISABLED') {
        return {
          initialCallRecordingToggleValue: false,
          callRecordingToggleVisible: true,
          callRecordingToggleDisabled: true,
        };
      }
      return {
        initialCallRecordingToggleValue: false,
        callRecordingToggleVisible: false,
        callRecordingToggleDisabled: true,
      };
    }

    if (twoPartyConsentRecordingSetting === 'REQUIRED_BOTH') {
      return {
        initialCallRecordingToggleValue: true,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: true,
      };
    }

    if (twoPartyConsentRecordingSetting === 'OPTIONAL_BOTH') {
      return {
        initialCallRecordingToggleValue: false,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: false,
      };
    }

    if (twoPartyConsentRecordingSetting === 'REQUIRED_USER') {
      return {
        initialCallRecordingToggleValue: true,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: true,
      };
    }
  }

  // !numberRequiresConsent - one party consent region
  if (onePartyConsentRecordingSetting === 'DISABLED') {
    if (twoPartyConsentRecordingSetting !== 'DISABLED') {
      return {
        initialCallRecordingToggleValue: false,
        callRecordingToggleVisible: true,
        callRecordingToggleDisabled: true,
      };
    }
    return {
      initialCallRecordingToggleValue: false,
      callRecordingToggleVisible: false,
      callRecordingToggleDisabled: true,
    };
  }

  if (onePartyConsentRecordingSetting === 'OPTIONAL_BOTH') {
    return {
      initialCallRecordingToggleValue: false,
      callRecordingToggleVisible: true,
      callRecordingToggleDisabled: false,
    };
  }

  if (onePartyConsentRecordingSetting === 'REQUIRED_BOTH') {
    return {
      initialCallRecordingToggleValue: true,
      callRecordingToggleVisible: true,
      callRecordingToggleDisabled: true,
    };
  }

  return defaultToggleSettings;
};

export const callRecordingRequired = (
  featureFlag: boolean,
  callRecordingToggleDisabled: boolean,
  initialCallRecordingToggleValue: boolean,
  callRecordingSetting: CallRecordingSetting | null | undefined,
): boolean => {
  return featureFlag
    ? callRecordingToggleDisabled && initialCallRecordingToggleValue
    : callRecordingSetting === 'REQUIRED';
};

export const shouldDisplayCallRecordingNotSetupTooltip = (
  featureFlag: boolean,
  onePartyConsentRecordingSetting: OnePartyConsentRecording | null | undefined,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
  callRecordingSetting: CallRecordingSetting | null | undefined,
): boolean => {
  return featureFlag
    ? onePartyConsentRecordingSetting === null ||
        twoPartyConsentRecordingSetting === null
    : callRecordingSetting === null;
};

// Function to get the helper text for one-sided call recording, it's used
// in both <ToggleSection/> and in <LiveCallRecording/>
export const determineCallRecordingHelperText = (
  featureFlag: boolean,
  isNumberValidated: boolean,
  numberRequiresConsent: boolean,
  callRecordingRequired: boolean,
  onePartyConsentRecordingSetting: OnePartyConsentRecording | null | undefined,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
): string => {
  // Empty string (instead of null) is returned for purpose of
  // not displaying the tooltip helper text in <TooltipHost/> component,
  // if it gets an empty string then it won't render the helper text

  // Cases covered:
  // 1. Feature flag off
  if (!featureFlag) {
    if (callRecordingRequired) {
      return 'Call Recording is forced by your Admin';
    }
    return '';
  }

  // Cases for feature flag on and validated number
  if (isNumberValidated && featureFlag) {
    // 2. Call recording is disabled for both one-party AND two-party regions
    if (
      onePartyConsentRecordingSetting === 'DISABLED' &&
      twoPartyConsentRecordingSetting === 'DISABLED'
    ) {
      return '';
    }

    // Cases for one-party consent regions
    if (!numberRequiresConsent) {
      // 3. Call recording is disabled for one party consent regions,
      // but not disabled for two-party consent regions
      if (
        onePartyConsentRecordingSetting === 'DISABLED' &&
        twoPartyConsentRecordingSetting !== 'DISABLED'
      ) {
        return 'Your organization disabled call recording for all calls into one-party consent regions.';
      }
      // 4. Call recording is optional for one-party consent regions
      if (onePartyConsentRecordingSetting === 'OPTIONAL_BOTH') {
        return '';
      }

      // 5. Call recording for one party is required
      if (onePartyConsentRecordingSetting === 'REQUIRED_BOTH') {
        return 'Your organization requires call recording for all calls into one-party consent regions.';
      }
    }

    // Cases for two-party consent regions
    if (numberRequiresConsent) {
      // 6. Call recording is not disabled for one party consent regions,
      // but disabled for two-party consent regions
      if (
        onePartyConsentRecordingSetting !== 'DISABLED' &&
        twoPartyConsentRecordingSetting === 'DISABLED'
      ) {
        return 'Your organization disabled call recording for all calls into two-party consent regions.';
      }

      // 7. Call recording is optional for one-party consent regions
      if (twoPartyConsentRecordingSetting === 'OPTIONAL_BOTH') {
        return '';
      }

      // 8. Call recording is optional required for both caller and recipient
      if (twoPartyConsentRecordingSetting === 'REQUIRED_BOTH') {
        return 'Your organization requires call recording for all calls into two-party consent regions.';
      }

      // 9. Call recording is optional required for user
      if (twoPartyConsentRecordingSetting === 'REQUIRED_USER') {
        return 'Your organization requires call recording for all calls into two-party consent regions. Only your voice will be recorded.';
      }
    }
  }

  return '';
};

export const determineLiveCallRecordingToolTipText = (
  oneSidedCallRecordingFeatureFlag: boolean,
  isNumberValidated: boolean,
  numberRequiresConsent: boolean,
  callRecordingRequired: boolean,
  onePartyConsentRecordingSetting: OnePartyConsentRecording | null | undefined,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
  callInProgress: boolean,
) => {
  if (callInProgress) {
    return determineCallRecordingHelperText(
      oneSidedCallRecordingFeatureFlag,
      isNumberValidated,
      numberRequiresConsent,
      callRecordingRequired,
      onePartyConsentRecordingSetting,
      twoPartyConsentRecordingSetting,
    );
  }
  if (!callRecordingRequired) {
    return 'You cannot record a call until it starts';
  }
  return '';
};

export const shouldDisplayTwoPartyRecordingConsentBanner = (
  numberRequiresConsent: boolean,
  oneSidedCallRecordingFeatureFlag: boolean,
  orgCallRecOverrideSetting: CallRecordingOverrideSetting | undefined,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
): boolean => {
  if (oneSidedCallRecordingFeatureFlag) {
    return (
      twoPartyConsentRecordingSetting !== 'DISABLED' && numberRequiresConsent
    );
  }

  return numberRequiresConsent && orgCallRecOverrideSetting === 'OPTIONAL';
};

export const twoPartyRecordingConsentBannerMessage = (
  oneSidedCallRecordingFeatureFlag: boolean,
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined,
): string => {
  if (oneSidedCallRecordingFeatureFlag) {
    if (twoPartyConsentRecordingSetting === 'REQUIRED_USER') {
      return 'You are calling into a two-party consent region. Only your voice will be recorded.';
    }
    if (
      twoPartyConsentRecordingSetting === 'REQUIRED_BOTH' ||
      twoPartyConsentRecordingSetting === 'OPTIONAL_BOTH'
    ) {
      return 'You are calling into a two-party consent region. An automated message will notify the recipient that the call is being recorded.';
    }
    if (twoPartyConsentRecordingSetting === 'DISABLED') {
      return '';
    }
  }
  return 'You are calling into a two-party consent region. Please get consent before recording.';
};

export const getStoredCallRecordedPreference = (): boolean | null => {
  const storedValue = localStorage.getItem(CALL_RECORDED_STORAGE_KEY);
  return storedValue !== null ? JSON.parse(storedValue) : null;
};

export const saveCallRecordedPreference = (value: boolean): void => {
  localStorage.setItem(CALL_RECORDED_STORAGE_KEY, JSON.stringify(value));
};

export const clearCallRecordedPreference = (): void => {
  localStorage.removeItem(CALL_RECORDED_STORAGE_KEY);
};

export const shouldRenderLiveCallRecording = (
  oneSidedCallRecordingFeatureFlag: boolean,
  callRecordingSetting: CallRecordingSetting | null | undefined,
  callRecordingToggleVisible: boolean,
): boolean => {
  if (!oneSidedCallRecordingFeatureFlag) {
    if (callRecordingSetting) {
      return ['OPTIONAL', 'REQUIRED'].includes(callRecordingSetting);
    }
  }

  return callRecordingToggleVisible;
};
